import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Panel, ModuleHeader } from "@/components/kcc/Panel";
import { StatusChip } from "@/components/kcc/Kpi";
import { Landmark, Lock, FileSignature, ShieldCheck, Loader2 } from "lucide-react";

export default function BoardPortal() {
    const { user } = useAuth();
    const [packs, setPacks] = useState(null);
    const [err, setErr] = useState("");
    useEffect(() => {
        api.get("/board/packs").then(({ data }) => setPacks(data.packs)).catch((e) => setErr(e.response?.data?.detail || "Restricted"));
    }, []);

    if (err) {
        return (
            <div className="max-w-2xl mx-auto py-16">
                <div className="kcc-panel kcc-red-accent p-8 text-center">
                    <Lock className="w-10 h-10 text-kcc-red mx-auto" />
                    <div className="kcc-label mt-4 text-kcc-red">Board Portal Restricted</div>
                    <h2 className="font-display text-2xl font-semibold text-white mt-2">Elevated access required</h2>
                    <p className="kcc-text-silver text-sm mt-3">{err}. Board papers are restricted to Board members, executive officers, the CISO and audit. Sign in as a Board demo role to review.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <ModuleHeader
                kicker="Board Portal™"
                title="Kamoto Copper Company · Board Papers"
                description="Confidential board and committee packs, resolutions, decision registers and minutes — with digital signatures, watermarking, read receipts and download controls."
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="kcc-panel kcc-purple-accent p-4 flex items-center gap-3"><Landmark className="w-5 h-5 text-kcc-purple" /><div><div className="kcc-label">Board member</div><div className="font-display text-sm text-white">{user?.name}</div></div></div>
                <div className="kcc-panel kcc-purple-accent p-4 flex items-center gap-3"><ShieldCheck className="w-5 h-5 text-kcc-green" /><div><div className="kcc-label">Session security</div><div className="font-display text-sm text-white">Watermarked · MFA-verified</div></div></div>
                <div className="kcc-panel kcc-purple-accent p-4 flex items-center gap-3"><FileSignature className="w-5 h-5 text-kcc-copper-light" /><div><div className="kcc-label">Digital signature</div><div className="font-display text-sm text-white">Available on resolutions</div></div></div>
            </div>

            <Panel title="Board Packs" subtitle="Latest committee and full Board packs" accent="purple">
                {!packs ? <div className="flex items-center justify-center py-10 kcc-text-silver"><Loader2 className="w-4 h-4 animate-spin mr-2" />Loading…</div> :
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {packs.map((p) => (
                            <div key={p.id} className="kcc-surface-elev p-5" data-testid={`pack-${p.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="kcc-label text-kcc-copper-light">{p.committee}</div>
                                        <div className="font-display text-lg font-semibold text-white mt-1">{p.title}</div>
                                        <div className="text-[11px] kcc-text-silver mt-1">Meeting {p.date} · {p.papers} papers · {p.resolutions} resolutions</div>
                                    </div>
                                    <StatusChip status={p.status === "Published" ? "normal" : "warning"}>{p.status}</StatusChip>
                                </div>
                                <div className="mt-4 flex flex-wrap gap-2 text-[11px]">
                                    <span className="kcc-chip executive">{p.classification}</span>
                                    <span className="kcc-chip info">Watermarked</span>
                                    <span className="kcc-chip normal">Read receipts</span>
                                </div>
                                <div className="mt-4 flex gap-2">
                                    <button className="text-xs bg-kcc-copper hover:bg-kcc-copper-light text-white rounded px-3 py-1.5" data-testid={`open-${p.id}`}>Open pack</button>
                                    <button className="text-xs bg-white/[0.04] border border-white/10 hover:border-white/25 text-white rounded px-3 py-1.5">Download (audited)</button>
                                </div>
                            </div>
                        ))}
                    </div>
                }
            </Panel>
        </div>
    );
}
